import { CloseCircleFilled, InfoCircleOutlined } from '@ant-design/icons';
import { Visibility } from '@mui/icons-material';
import { captureException } from '@sentry/react';
import { Tooltip } from 'antd';
import { getCurrentUser } from 'aws-amplify/auth';
import useStateManager from 'components/hooks/useStateManager';
import { filter, isEmpty } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { useCurrentClass } from 'store/classes/selectors';
import { useCurrentOrg } from 'store/organizations/selectors';
import { useUserClasses } from 'store/userClasses/selectors';
import styled from 'styled-components';
import color from 'utils/constants/color';
import ContentLoading from './contentLoading';
import FilterRecords from './filterRecords';
import FilterTable from './filterTable';
import { sortData, upcomming_year } from './helper';
import useStandardExam from 'components/hooks/books/useStandardExam';
import useGetStudentFromClass from 'components/hooks/useGetStudentFromClass/useGetStudentFromClass';
import { handleGetFilterData, handleListGetSchool } from 'store/organizations/thunks';
import { handleListCourseInfo } from 'store/standardexams';
import usePermissionForUser from 'components/hooks/permissions/usePermissionForUser';
import {
  handleFeedBackReport,
  handleGetAssignmentStanard,
  handleGetOverallProgress,
  handleGetStudentFeedBackReport,
} from 'store/exams/thunks';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import ViewStudentDetail from './viewStudentDetail';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import StandardExamNameModal from 'modules/standardExamReport/StandardExamNameModal';
import useBadWords from "components/hooks/words/badWords";
import SentryWrap from 'components/Sentry';

const mapProficiencyFilterTeachersToOptions = (rows) => {
  if (!Array.isArray(rows)) return [];
  const seen = new Set();
  const out = [];
  for (const row of rows) {
    const id = row?.id;
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const lastName = typeof row.lastName === 'string' ? row.lastName.trim() : '';
    const firstName = typeof row.firstName === 'string' ? row.firstName.trim() : '';
    const displayRaw = typeof row.displayName === 'string' ? row.displayName.trim() : '';
    const displayName = displayRaw.replace(/\n/g, ' ').trim();
    let label = '';
    if (lastName || firstName) {
      label = [lastName, firstName].filter(Boolean).join(', ');
    } else if (displayName) {
      label = displayName;
    } else {
      label = String(id);
    }
    out.push({ label, value: id });
  }
  return out;
};

function StandardExamReport() {
  const {getTextFromHtml} = useBadWords();
  const showAlert = useAlert();
  const dispatch = useDispatch();
  const currentOrg = useCurrentOrg();
  const navigate = useNavigate();
  const userClasses = useUserClasses();
  const currentClass = useCurrentClass();
  const { standardExam, basedOnGradeStandardExam } = useStandardExam();
  const { getClassStudent, getFetchStudent } = useGetStudentFromClass();
  const schools = useSelector((state) => state.schools);
  const { isDistrictAdmin } = usePermissionForUser();
  const location = useLocation();
  const search = useMemo(() => queryString.parse(location.search), [location.search]);
  const [openStandardExamNameModal, setOpenStandardExamNameModal] = useState(false);
  const activeGrade = useMemo(() => search?.grade, [search?.grade]);
  const preselectedGrade = useMemo(() =>{
    let values = {};
    if(Array.isArray(standardExam) && standardExam.length > 0){
      if(activeGrade){
        const item = standardExam.find((exam) => exam.displayName === activeGrade)
        if(item){
          values = item;
        }
      } else {
        values = standardExam[0];
      }
    }
    return values;
  }, [standardExam, activeGrade]);

  const [states, updateStates, updateNestedKey] = useStateManager({
    loading: { type: '', value: false },
    filterRecords: {
      selected_class: currentClass?.id,
      activeSession: upcomming_year,
      selected_grade_level: preselectedGrade?.displayName,
      selected_exam_book: basedOnGradeStandardExam(preselectedGrade?.displayName)?.[0]?.bookId,
      selected_sort_by: 'highest',
    },
    optionsData: {
      standardExam,
      gradeExamOptions: basedOnGradeStandardExam(preselectedGrade?.displayName),
      studentList: getClassStudent(currentClass?.id) || [],
      proficiencyTeacherOptions: [],
      courseInfo: [],
    },
    allCourses: [],
    selectedCourse: null,
    result: [],
    filteredResult: [],
    overallReport: {},
  });

  const fetchDistrictData = async () => {
    try {
      if (!isEmpty(currentOrg)) {
        dispatch(handleListGetSchool(currentOrg.id));
      }
    } catch (error) {
      captureException(`471 Error in fetchDistrictData: ${error}`);
    }
  };

  useEffect(() => {
    if (isDistrictAdmin) {
      fetchDistrictData();
    }
  }, [currentOrg]);

  useEffect(() => {
    const fetchCourseInfo = async () => {
      if (currentOrg?.id && isDistrictAdmin) {
        try {
          const result = await dispatch(handleListCourseInfo({ organisationId: currentOrg.id }));
          if (result?.data) {
            updateNestedKey(['optionsData', 'courseInfo'], result.data);
          }
        } catch (error) {
          captureException(`Error fetching course info: ${error}`);
        }
      }
    };
    fetchCourseInfo();
  }, [currentOrg, isDistrictAdmin, dispatch]);

  const school_option = useMemo(() => {
    const values = [];
    if (!isEmpty(schools)) {
      values.push(
        ...schools.filter((k) => !k.demo).map((v) => ({ label: v.displayName, value: v.id })),
      );
    }
    return values;
  }, [schools]);

  const teacher_map = useMemo(
    () =>
      Array.isArray(states?.optionsData?.proficiencyTeacherOptions)
        ? states.optionsData.proficiencyTeacherOptions
        : [],
    [states?.optionsData?.proficiencyTeacherOptions],
  );

  useEffect(() => {
    if (search && search.orgId) {
      const val = school_option.find((k) => k.value == search.orgId);
      if (val) {
        updateNestedKey(['filterRecords', 'selected_school'], val.value);
        fetchTeacher(val.value, search.teacherId);
        return;
      }
    }
    const opt = school_option[0];
    if (opt) {
      updateNestedKey(['filterRecords', 'selected_school'], opt.value);
      fetchTeacher(opt.value);
    }
  }, [school_option, search]);

  const fetchTeacher = async (orgId, activeTeacher) => {
    try {
      if (!isDistrictAdmin) {
        return;
      }
      const result = await handleGetFilterData(orgId, null, 'teacher');
      const opts = mapProficiencyFilterTeachersToOptions(result?.data);
      updateNestedKey(['optionsData', 'proficiencyTeacherOptions'], opts);

      if (result && result.success && Array.isArray(result.data) && result.data.length > 0) {
        if (activeTeacher) {
          const activeTeacherData = result.data.find((k) => k.id === activeTeacher);
          if (activeTeacherData) {
            updateNestedKey(['filterRecords', 'selected_teacher'], activeTeacherData.id);
            await fetchTeacherClasses(activeTeacherData.id);
          } else {
            const s_teacher = result.data[0];
            updateNestedKey(['filterRecords', 'selected_teacher'], s_teacher.id);
            await fetchTeacherClasses(s_teacher.id);
          }
        } else {
          const s_teacher = result.data[0];
          if (s_teacher) {
            updateNestedKey(['filterRecords', 'selected_teacher'], s_teacher.id);
            await fetchTeacherClasses(s_teacher.id);
          }
        }
      } else {
        updateNestedKey(['filterRecords', 'selected_teacher'], null);
        updateStates({
          allCourses: [],
          selectedCourse: null,
          selectedTeacherClasses: [],
        });
      }
    } catch (error) {
      captureException(error.message, 'error in fetching school teacher');
      updateNestedKey(['optionsData', 'proficiencyTeacherOptions'], []);
      updateNestedKey(['filterRecords', 'selected_teacher'], null);
      updateStates({
        allCourses: [],
        selectedCourse: null,
        selectedTeacherClasses: [],
      });
    } finally {
    }
  };

  const fetchTeacherClasses = async (teacher_id) => {
    try {
      if (!isDistrictAdmin) {
        return;
      }
      const result = await handleGetFilterData(null, teacher_id, 'classes');
      if (result && result.success) {
        const uniqueCourses = result.data?.reduce((acc, curr) => {
          if (!acc.find(c => c.value === curr.course_info?.id)) {
            acc.push({
              label: curr.course_info?.title || '',
              value: curr.course_info?.id,
              classes: [{ id: curr.id, displayName: curr.displayName, code: curr.code }]
            });
          } else {
            acc.find(c => c.value === curr.course_info?.id).classes.push({
              id: curr.id,
              displayName: curr.displayName,
              code: curr.code
            });
          }
          return acc;
        }, []);
        updateStates({
          allCourses: uniqueCourses || [],
        });

        if (uniqueCourses && uniqueCourses.length > 0) {
          const firstCourse = uniqueCourses[0];
          updateNestedKey(['selectedCourse'], firstCourse.value);
          const firstClass = firstCourse.classes?.[0];
          if (firstClass) {
            updateNestedKey(['filterRecords', 'selected_class'], firstClass.id);
          }
        } else if (result.data && result.data.length > 0) {
          const s_class = result.data[0];
          if (s_class) {
            updateNestedKey(['filterRecords', 'selected_class'], s_class.id);
          }
        }

        const dt = (result.data || []).map((k) => ({
          label: `${k.displayName || ''}`,
          value: k.id,
        }));
        updateStates({
          selectedTeacherClasses: dt,
        });
      }
    } catch (error) {
    } finally {
    }
  };

  const handleChangeGradeData = (value) => {
    try {
      const examData = basedOnGradeStandardExam(value);
      updateNestedKey(['optionsData', 'gradeExamOptions'], examData);
      updateNestedKey(['filterRecords', 'selected_exam_book'], examData[0]?.bookId);
    } catch (error) {
      captureException(error);
    }
  };

  useEffect(() => {
    if (userClasses) {
      const findActiveClass = filter(userClasses, (k) => !k?.archived);
      const dt = (findActiveClass || []).map((k) => ({
        label: `${k.displayName || ''}`,
        value: k.id,
      }));
      updateStates({
        selectedTeacherClasses: dt,
      });
    }
  }, [userClasses]);

  useEffect(() => {
    try {
      updateNestedKey(['filterRecords', 'selected_class'], currentClass?.id);
    } catch (error) {
      captureException(error);
    }
  }, [currentClass?.id]);

  const tableData = useMemo(() => {
    if (states?.result?.length > 0) {
      const dataToSort = [...states?.result];
      return sortData(
        dataToSort,
        states?.filterRecords?.selected_sort_by === 'highest' ? 'desc' : 'asc',
      );
    }
    return [];
  }, [states?.result, states?.filterRecords?.selected_sort_by]);

  const tableDataByClass = useMemo(() => {
    if (!states?.filterRecords?.selected_exam_book) return [];

    // If there are filtered results, use them; otherwise use original results
    const dataToUse = states?.filteredResult?.length > 0 ? states.filteredResult : states?.result;

    if (states?.filterRecords?.selected_class) {
      return dataToUse;
    } else {
      return tableData;
    }
  }, [
    tableData,
    states?.filterRecords?.selected_class,
    states?.filterRecords?.selected_exam,
    states?.result,
    states?.filteredResult,
  ]);

  const viewFeedBackTableDataByClass = useMemo(() => {
    if (states?.isFeedBackShow?.data && states?.isFeedBackShow?.open) {
      const allIncorrectAnswers = states?.isFeedBackShow?.data.flatMap(item => item?.incorrectAnswers);
      return allIncorrectAnswers;
    } else {
      return [];
    }
  }, [states?.isFeedBackShow?.data, states?.isFeedBackShow?.open]);

  const fetchStudentList = async () => {
    try {
      if (states?.loading?.value && states?.loading?.type === 'fetch') {
        return;
      }
      updateStates({ loading: { type: 'fetch', value: true } });
      //if (!isDistrictAdmin) return;
      const getStudentList = await getFetchStudent(states?.filterRecords?.selected_class);
      if (getStudentList?.length > 0) {
        updateNestedKey(['optionsData', 'studentList'], getStudentList);
      }
      updateStates({ loading: { type: 'fetch', value: false } });
    } catch (error) {
      console.log(error);
    } finally {
      updateStates({ loading: { type: 'fetch', value: false } });
    }
  };

  useEffect(() => {
    if(states?.isFeedBackShow?.open){
      fetchStudentList();
    }
  }, [states?.filterRecords?.selected_class, states?.isFeedBackShow?.open]);

  const handleSearch = (searchText) => {
    try {
      if (!searchText || searchText.trim() === '') {
        // If search is empty, show all results
        updateStates({ filteredResult: states.result });
        return;
      }

      const filteredData = states.result.filter((item) => {
        const firstName = item.user?.firstName || '';
        const lastName = item.user?.lastName || '';
        const fullName = `${firstName} ${lastName}`;

        // Remove all spaces from full name and search text
        const fullNameNoSpaces = fullName.replace(/\s/g, '').toLowerCase();
        const searchTextNoSpaces = searchText.replace(/\s/g, '').toLowerCase();

        return (
          fullNameNoSpaces.includes(searchTextNoSpaces) ||
          firstName.toLowerCase().includes(searchTextNoSpaces) ||
          lastName.toLowerCase().includes(searchTextNoSpaces)
        );
      });

      updateStates({ filteredResult: filteredData });
    } catch (error) {
      captureException(`474 Error in handleSearch: ${error}`);
      console.error('An error occurred while filtering data:', error);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'user',
      key: 'user',
      width: 140,
      render: (user, record) => (
        <div
          style={{
            marginRight: 8,
            display: 'flex',
            alignItems: 'center',
            whiteSpace: 'nowrap',   // 👈 prevents line break
            gap: 6,
            // justifyContent: 'center',
          }}
          onClick={() => handleToggleStandardExamNameModal(true,user,record)}
        >
          <span
            style={{
              display: 'flex',
              alignItems: 'center',     
              cursor: 'pointer',
              gap: 4,                   
            }}
          >
            <span style={{ fontSize: 12, color: color.black}}>{user?.firstName || ''} {user?.lastName || ''}</span>
            <VisibilityOutlinedIcon/>
          </span>
        </div>
      ),
    },
    {
      title: 'Overall Score',
      onHeaderCell: () => ({
        className: 'score-header',
      }),
      dataIndex: 'overallStats',
      key: 'overallScore',
      width: 140,
      render: (overallStats) => (
        <span style={{ marginRight: 8, textAlign: 'center', display: 'block' }}>
          {overallStats?.overallScore || 0}%
        </span>
      ),
    },
    {
      title: 'Proficiency Level',
      onHeaderCell: () => ({
        className: 'score-header',
      }),
      dataIndex: 'overallStats',
      key: 'proficiency_level',
      width: 140,
      render: (overallStats) => (
        <span style={{ textAlign: 'center', display: 'block' }}>
          {overallStats?.proficiency_level
            ? String(overallStats.proficiency_level).replace(/_/g, ' ')
            : '-'}
        </span>
      ),
    },
    {
      title: 'Section 1 Score',
      onHeaderCell: () => ({
        className: 'score-header',
      }),
      dataIndex: 'assignmentData',
      width: 140,
      key: 'section1',
      render: (assignmentData, record) => {
        const section1 = assignmentData?.[0];
        if (section1?.score == null) {
          return (
            <span style={{ color: color.green, textAlign: 'center', display: 'block' }}>{'-'}</span>
          );
        }
        return (
          <span
            onClick={() => handleOpenAssignmentScore(section1, record?.user?.id)}
            style={{ color: color.green, textAlign: 'center', display: 'block', cursor: 'pointer' }}
          >
            {section1?.status === 'Submitted' ? section1?.score + '%' || 0 : '-'}
          </span>
        );
      },
    },
    {
      title: 'Section 2 Score',
      onHeaderCell: () => ({
        className: 'score-header',
      }),
      dataIndex: 'assignmentData',
      width: 140,
      key: 'section2',
      render: (assignmentData, record) => {
        const section2 = assignmentData?.[1]; // Second assignment
        if (section2?.score == null) {
          return (
            <span style={{ color: color.green, textAlign: 'center', display: 'block' }}>{'-'}</span>
          );
        }
        return (
          <span
            onClick={() => handleOpenAssignmentScore(section2, record?.user?.id)}
            style={{ color: color.green, textAlign: 'center', display: 'block', cursor: 'pointer' }}
          >
            {section2?.status === 'Submitted' ? section2?.score + '%' || 0 : '-'}
          </span>
        );
      },
    },
    {
      title: 'Section 3 Score',
      onHeaderCell: () => ({
        className: 'score-header',
      }),
      dataIndex: 'assignmentData',
      key: 'section3',
      width: 140,
      render: (assignmentData, record) => {
        const section3 = assignmentData?.[2]; // Third assignment
        if (section3?.score == null) {
          return (
            <span style={{ color: color.green, textAlign: 'center', display: 'block' }}>{'-'}</span>
          );
        }
        return (
          <span
            onClick={() => handleOpenAssignmentScore(section3, record?.user?.id)}
            style={{ color: color.green, textAlign: 'center', display: 'block', cursor: 'pointer' }}
          >
            {section3?.status === 'Submitted' ? section3?.score + '%' || 0 : '-'}
          </span>
        );
      },
    },
    {
      title: 'Section 4 Score',
      onHeaderCell: () => ({
        className: 'score-header',
      }),
      dataIndex: 'assignmentData',
      width: 140,
      key: 'section4',
      render: (assignmentData, record) => {
        const section4 = assignmentData?.[3]; // Fourth assignment
        if (section4?.score == null) {
          return (
            <span style={{ color: color.green, textAlign: 'center', display: 'block' }}>{'-'}</span>
          );
        }
        return (
          <span
            onClick={() => handleOpenAssignmentScore(section4, record?.user?.id)}
            style={{ color: color.green, textAlign: 'center', display: 'block', cursor: 'pointer' }}
          >
            {section4?.status === 'Submitted' ? section4?.score + '%' || 0 : '-'}
          </span>
        );
      },
    },
    {
      title: 'Questions Passed',
      onHeaderCell: () => ({
        className: 'score-header',
      }),
      dataIndex: 'overallStats',
      width: 140,
      key: 'totalCorrect',
      render: (overallStats) => (
        <span style={{ color: color.green, textAlign: 'center', display: 'block' }}>
          {overallStats?.totalCorrect || 0}
        </span>
      ),
    },
    {
      title: 'Questions Failed',
      onHeaderCell: () => ({
        className: 'score-header',
      }),
      dataIndex: 'overallStats',
      width: 140,
      key: 'totalIncorrect',
      render: (overallStats, record) => {
        const check = record?.assignmentData?.every((item) => item?.status === 'Not Attempted');
        if (check) {
          return (
            <span style={{ color: color.red, textAlign: 'center', display: 'block' }}>{'-'}</span>
          );
        }
        return (
          <span style={{ color: color.red, textAlign: 'center', display: 'block' }}>
            {overallStats?.totalIncorrect || 0}
          </span>
        );
      },
    },
    {
      title: 'FeedBack Report',
      dataIndex: 'feedBack',
      width: 140,
      key: 'feedBack',
      onHeaderCell: () => ({
        className: 'feedback-header',
      }),
      render: (user) => (
        <span
          onClick={() => handleFeedBakShow(user)}
          style={{ textAlign: 'center', display: 'block' }}
        >
          <Visibility style={{ fontSize: 30, color: color.black, cursor: 'pointer' }} />
        </span>
      ),
    },
  ];



  const handleToggleStandardExamNameModal = (value,user,record) => {
    if(value){
      updateStates({ overallReport: {score: record?.overallStats?.overallScore, name: user?.firstName + ' ' + user?.lastName, standardReport: record?.overallReportStandardData} });
      setOpenStandardExamNameModal(true);
      return;
    }
    updateStates({ overallReport: {} });
    setOpenStandardExamNameModal(false);
  };

  const handleFeedBakShow = (data) => {
    fetchStudentData(data);
  };

  const viewDetailsColumns = [
    {
      title: 'Question',
      dataIndex: 'incorrectAnswers',
      key: 'question',
      width: 200,
      onHeaderCell: () => ({
        className: 'score-header',
      }),
      render: (_,record) => {
        return (
          <span
            style={{
              marginRight: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              justifyContent: 'center',
            }}
          >
            <div style={{ textAlign: 'center' }}>
               <div dangerouslySetInnerHTML={{ __html: record?.question?.question || 'Question not available'}} style={{ fontWeight: 'bold', marginBottom: '4px' }}>
              </div>

              {/* {firstIncorrect?.question?.standard && (
                <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
                  Standard: {firstIncorrect.question.standard}
                </div>
              )} */}
            </div>
          </span>
        );
      },
    },
    {
      title: 'Correct Answer',
      dataIndex: 'incorrectAnswers',
      key: 'correctAnswer',
      width: 180,
      render: (_,record) => {
        const correctIndex = record?.question?.answer;
        const correctOption = record?.question?.options?.[correctIndex];
        return (
          <span
            style={{
              marginRight: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              justifyContent: 'center',
            }}
          >
            <span
              style={{
                color: color.green,
                fontWeight: 'bold',
                textAlign: 'center',
                display: 'block',
              }}
            >
              {correctOption || 'N/A'}
            </span>
          </span>
        );
      },
    },
    {
      title: 'Answer Chosen',
      dataIndex: 'incorrectAnswers',
      width: 180,
      key: 'answerChosen',
      render: (_,record) => {
        const chosenIndex = record?.answer;
        const chosenOption = record?.question?.options?.[chosenIndex];
        return (
          <span
            style={{ color: color.red, fontWeight: 'bold', textAlign: 'center', display: 'block' }}
          >
            {chosenOption || 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Possible Misconception',
      dataIndex: 'incorrectAnswers',
      width: 220,
      key: 'misconception',
      render: (_,record) => {
        const misconception = record?.standard_feedback?.possible_misconception;
        return (
          <span
            style={{
              color: color.black,
              fontWeight: '600',
              fontSize: '14px',
              lineHeight: '1.4',
              textAlign: 'center',
              display: 'block',
            }}
          >
            {misconception || 'No misconception data available'}
          </span>
        );
      },
    },
    {
      title: 'Strategy 1',
      dataIndex: 'incorrectAnswers',
      key: 'strategy1',
      width: 220,
      render: (_,record) => {
        const strategy1 = record?.standard_feedback?.strategy1;
        return (
          <span
            style={{
              color: color.green,
              fontWeight: '600',
              fontSize: '14px',
              lineHeight: '1.4',
              textAlign: 'center',
              display: 'block',
            }}
          >
            {strategy1 || 'No strategy data available'}
          </span>
        );
      },
    },
    {
      title: 'Strategy 2',
      dataIndex: 'incorrectAnswers',
      width: 220,
      key: 'strategy2',
      render: (_,record) => {
        const strategy2 = record?.standard_feedback?.strategy2;
        return (
          <span
            style={{
              color: color.green,
              fontWeight: '600',
              fontSize: '14px',
              lineHeight: '1.4',
              textAlign: 'center',
              display: 'block',
            }}
          >
            {strategy2 || 'No strategy data available'}
          </span>
        );
      },
    },
    {
      title: 'Strategy 3',
      dataIndex: 'incorrectAnswers',
      width: 220,
      key: 'strategy3',
      render: (_,record) => {
        const strategy3 = record?.standard_feedback?.strategy3;
        return (
          <span
            style={{
              color: color.green,
              fontWeight: '600',
              fontSize: '14px',
              lineHeight: '1.4',
              textAlign: 'center',
              display: 'block',
            }}
          >
            {strategy3 || 'No strategy data available'}
          </span>
        );
      },
    },
  ];

  const fetchFilterData = async () => {
    try {
      updateStates({ loading: { type: 'fetch', value: true } });
      const payload = {
        class_id: states?.filterRecords?.selected_class,
        exam_book_id: states?.filterRecords?.selected_exam_book,
        grade_level: states?.filterRecords?.selected_grade_level,
        session: states?.filterRecords?.activeSession,
        order: states?.filterRecords?.selected_sort_by === 'highest' ? 'desc' : 'asc',
      };
      const data = await dispatch(handleFeedBackReport(payload));
      if (data?.success) {
        const resultWithFeedback = (data?.result || []).map((item) => ({
          ...item,
          feedBack: item,
        }));
        const totalQuestions = resultWithFeedback[0].overallStats.totalQuestions;

        updateStates({
          result: resultWithFeedback,
          filteredResult: resultWithFeedback,
          numberOfQuestions: totalQuestions || 0,
        });
      } else {
        updateStates({ result: [], filteredResult: [] });
      }
    } catch (error) {
      captureException(error);
    } finally {
      updateStates({ loading: { type: 'fetch', value: false } });
    }
  };

  const fetchStudentData = async (data) => {
    try {
      updateStates({ loading: { type: 'fetch', value: true } });
      const payload = {
        classId: states?.filterRecords?.selected_class,
        exam_book_id: states?.filterRecords?.selected_exam_book,
        studentId: data?.user?.id,
      };
      const studentData = await dispatch(handleGetStudentFeedBackReport(payload));
      if (studentData?.success) {
        updateStates({
          isFeedBackShow: { open: true, data: studentData?.result || [] },
        });
        const selectedId = data?.user?.id;
        if (selectedId) {
          updateNestedKey(['filterRecords', 'selected_student'], selectedId);
        }
        return studentData;
      } else {
        const selectedId = data?.user?.id;
        updateStates({
          isFeedBackShow: { open: true, data:  [] },
        });
        updateNestedKey(['filterRecords', 'selected_student'], selectedId);
        showAlert(studentData?.error, 'warning');
      }
    } catch (error) {
      captureException(error);
    } finally {
      updateStates({ loading: { type: 'fetch', value: false } });
    }
  };

  useEffect(() => {
    fetchFilterData();
  }, [
    states?.filterRecords?.selected_class,
    states?.filterRecords?.selected_exam_book,
    states?.filterRecords?.selected_grade_level,
    states?.filterRecords?.selected_sort_by,
  ]);

  useEffect(() => {
    async function fetchData() {
      try {
        await getCurrentUser();
      } catch (error) {
        captureException(`470 Error in fetchData: ${error}`);
      }
    }
    fetchData();
  }, []);

  const ExamUnitCompareColumns = [
    {
      title: 'Questions',
      dataIndex: 'question',
      key: 'question',
      width: 300,
      render: (question) => {
        return (
          <span className='flex items-center'>
            <div dangerouslySetInnerHTML={{ __html: question || 'Question not available' }} style={{ fontSize: '12px', color: '#666' }} />
            <InfoTooltip message={getTextFromHtml(question) || 'Question not available'} />
          </span>
        );
      },
    },
    {
      title: 'Passing %',
      dataIndex: 'pass_percent',
      key: 'pass_percent',
      width: 120,
      render: (value) => (
        <span style={{ color: color.green, fontWeight: 'bold' }}>{value || '0'}%</span>
      ),
    },
    {
      title: 'Failing %',
      dataIndex: 'fail_percent',
      key: 'fail_percent',
      width: 120,
      render: (value) => (
        <span style={{ color: color.red, fontWeight: 'bold' }}>{value || '0'}%</span>
      ),
    },
    // {
    //   title: 'Passed',
    //   dataIndex: 'passed',
    //   key: 'passed',
    //   width: 100,
    //   render: (value) => <span style={{ color: color.green }}>{value || '0'}</span>,
    // },
    // {
    //   title: 'Failed',
    //   dataIndex: 'failed',
    //   key: 'failed',
    //   width: 100,
    //   render: (value) => <span style={{ color: color.red }}>{value || '0'}</span>,
    // },
    {
      title: 'Attempted',
      dataIndex: 'attempted',
      key: 'attempted',
      width: 100,
      render: (value) => <span style={{ color: color.black }}>{value || '0'}</span>,
    },
  ];

  const handleOnCancel = (value) => {
    updateStates({ assignmentScore: { open: value, data: [] } });
  };

  const handleGetAssignmentScore = async (data) => {
    try {
      const payload = {
        ...data,
        class_id: states?.filterRecords?.selected_class,
      };
      const response = await dispatch(handleGetAssignmentStanard(payload));
      if (response) {
        return response;
      } else {
        return { success: false, result: [] };
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleOpenAssignmentScore = (data, userId) => {
    updateStates({ assignmentScore: { open: true, data: data, userId: userId } });
  };
  

  const fetchOverallProgress = async () => {
    const payload = {
      book_id: states?.filterRecords?.selected_exam_book,
      district_id: !isDistrictAdmin ? currentOrg?.parentId : currentOrg?.id,
    };
    const result = await dispatch(handleGetOverallProgress(payload));
    if (result?.statusCode === 200) {
      updateStates({ overallProgress: result?.question_stats });
    } else {
      updateStates({ overallProgress: [] });
    }
  };

  useEffect(() => {
    if (states?.filterRecords?.selected_exam_book) {
      fetchOverallProgress();
    }
  }, [states?.filterRecords?.selected_exam_book]);

  return (
    <SentryWrap>
    <div className="unitExamPageData" style={{ position: 'relative' }}>
      <div className="closeUnitExamUpr">
        <CloseCircleFilled
          onClick={() => {
            if (states?.isFeedBackShow?.open) {
              updateStates({
                isFeedBackShow: { open: false, data: [] },
              });
            } else {
              navigate(-1);
            }
          }}
          style={{ color: 'red' }}
          className="closeUnitExamIcon"
        />
      </div>

      <ViewStudentDetail
        open={states?.assignmentScore?.open}
        onCancel={() => handleOnCancel(false)}
        data={states?.assignmentScore?.data}
        userId={states?.assignmentScore?.userId}
        funcObj={{ handleGetAssignmentScore }}
      />

      <ContentLoading isLoading={states?.loading?.value && states?.loading?.type === 'fetch'} />

      {states?.isFeedBackShow?.open ? <StyledButton>Standards-Based Exam </StyledButton> : null}
      <FilterRecords
        columns={columns}
        data={states}
        filterRecords={states?.filterRecords}
        updateNestedKey={updateNestedKey}
        funcObj={{
          handleChangeGradeData,
          fetchTeacherClasses,
          fetchStudentData,
          fetchTeacher,
          updateStates,
        }}
        teacher_map={teacher_map}
        school_option={school_option}
        allCourses={states?.allCourses || []}
        selectedCourse={states?.selectedCourse}
      />

      <FilterTable
        number_of_questions={states?.numberOfQuestions}
        tableDataByClass={
          states?.isFeedBackShow?.open ? viewFeedBackTableDataByClass : tableDataByClass
        }
        handleSearch={handleSearch}
        columns={states?.isFeedBackShow?.open ? viewDetailsColumns : columns}
        data={states}
        examUnitCompareColumns={ExamUnitCompareColumns}
        examUnitCompareData={states?.overallProgress || []}
      />
      {openStandardExamNameModal && (
        <StandardExamNameModal
          overallReport={states?.overallReport || {}}
          handleToggleStandardExamNameModal={(value) => handleToggleStandardExamNameModal(value)}
        />
      )}
    </div>
    </SentryWrap>
  );
}

export default StandardExamReport;

const StyledButton = styled.button`
  background-color: #2f2eb7;
  color: white;
  height: 40px;
  width: 297px;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 20px;
  outline: none;
  border: none;
`;

const InfoTooltip = ({ message }) => (
  <Tooltip title={message || ''}>
    <InfoCircleOutlined style={{ marginLeft: 8, color: color.redishpink }} />
  </Tooltip>
);
